import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useViewMode } from '@/lib/view-mode-context';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { ArrowLeft, Flame, Snowflake, Trophy, TrendingDown, Share2 } from 'lucide-react';
import { cn, formatCurrency } from '@/lib/utils';
import { format } from 'date-fns';
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Cell,
  Tooltip,
  BarChart,
  Bar,
} from 'recharts';
import { calculatePlayerStats, getRadarData, getConsistencyRadarData } from '@/lib/player-stats';
import { computeBadges } from '@/lib/badges';
import { BadgeDisplay } from '@/components/BadgeDisplay';
import { Badge } from '@/components/ui/badge';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { useUserSettings } from '@/lib/user-settings-context';
import { usePlayer } from '@/hooks/use-players';
import { usePlayerSessionPlayers, useSessionsRanks } from '@/hooks/use-session-players';
import { usePlayerExpenses } from '@/hooks/use-expenses';

interface SessionEntry {
  sessionId: string;
  date: string;
  status: string;
  buyIns: number;
  buyInUnit: number;
  cashOut: number | null;
  net: number;
}

export default function PlayerProfile() {
  const { id } = useParams<{ id: string }>();
  const { isPublic, targetUserId } = useViewMode();
  const { settings } = useUserSettings();
  const navigate = useNavigate();

  const { data: player, isLoading: playerLoading } = usePlayer(id);
  const { data: playerSP = [], isLoading: spLoading } = usePlayerSessionPlayers(id);
  const { data: playerExpenses = [], isLoading: expensesLoading } = usePlayerExpenses(id);

  const sessionIds = useMemo(() => playerSP.map(sp => sp.session_id), [playerSP]);
  const { data: sessionRanks = [], isLoading: ranksLoading } = useSessionsRanks(sessionIds);

  const loading = playerLoading || spLoading || expensesLoading || ranksLoading;

  const profileData = useMemo(() => {
    if (loading || !player || !id) return null;

    const sessionExpenseMap: Record<string, number> = {};
    const paidExpenses = playerExpenses.filter(e => e.payer_id === id);
    const sharedExpenses = playerExpenses.flatMap(e => 
      (e.participants || []).filter((p: any) => p.player_id === id).map((p: any) => ({ ...p, expense: e }))
    );

    let globalExpenseNet = 0;
    paidExpenses.forEach(e => {
      if (e.session_id) {
        if (!sessionExpenseMap[e.session_id]) sessionExpenseMap[e.session_id] = 0;
        sessionExpenseMap[e.session_id] += Number(e.amount);
      } else {
        globalExpenseNet += Number(e.amount);
      }
    });

    sharedExpenses.forEach(se => {
      const sid = se.expense?.session_id;
      if (sid) {
        if (!sessionExpenseMap[sid]) sessionExpenseMap[sid] = 0;
        sessionExpenseMap[sid] -= Number(se.share);
      } else {
        globalExpenseNet -= Number(se.share);
      }
    });

    const sessions: SessionEntry[] = playerSP.map((sp: any) => ({
      sessionId: sp.session_id,
      date: sp.sessions?.date || '',
      status: sp.sessions?.status || 'unsettled',
      buyIns: sp.buy_ins,
      buyInUnit: sp.sessions?.buy_in_unit || 20,
      cashOut: sp.cash_out,
      net: (sp.net_amount || 0) + (sessionExpenseMap[sp.session_id] || 0),
    })).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    const completedIds = sessions
      .filter(e => e.status === 'settled' || e.status === 'completed') 
      .map(e => e.sessionId);

    let placementData: { name: string; count: number }[] = [];
    if (completedIds.length > 0 && sessionRanks.length > 0) {
      let top3 = 0, mid = 0, bottom3 = 0;
      completedIds.forEach(sid => {
        const results = sessionRanks
          .filter(sr => sr.session_id === sid)
          .sort((a, b) => Number(b.net_amount) - Number(a.net_amount));
        
        const rank = results.findIndex(sr => sr.player_id === id) + 1;
        const total = results.length;
        if (rank === 0) return;
        if (rank <= 3) top3++;
        else if (rank > total - 3) bottom3++;
        else mid++;
      });
      placementData = [
        { name: 'Top 3', count: top3 },
        { name: 'Middle', count: mid },
        { name: 'Bottom 3', count: bottom3 },
      ];
    }

    const stats = calculatePlayerStats(sessions, globalExpenseNet);
    const radarData = getRadarData(stats);
    const consistencyRadarData = getConsistencyRadarData(sessions);
    const badges = computeBadges(stats, sessions.map((s) => ({ net: s.net, buyIns: s.buyIns, buyInUnit: s.buyInUnit })));

    const trendData = [...sessions]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-10)
      .reduce((acc, session, i) => {
        const prevNet = i > 0 ? acc[i - 1].net : 0;
        acc.push({
          date: session.date ? format(new Date(session.date), 'MMM d') : 'Unknown',
          net: prevNet + session.net,
        });
        return acc;
      }, [] as { date: string; net: number }[]);

    const unsettled = sessions.filter((s) => s.status === 'unsettled');
    const totalBuyInAmount = unsettled.reduce((s, e) => s + e.buyIns * e.buyInUnit, 0);
    const totalNet = unsettled.reduce((s, e) => s + e.net, 0);

    return {
      playerName: player.name,
      nickname: player.nickname,
      avatarUrl: player.avatar_url,
      sessions,
      stats,
      radarData,
      consistencyRadarData,
      badges,
      trendData,
      totalBuyInAmount,
      totalNet,
      placementData
    };
  }, [loading, player, playerSP, playerExpenses, sessionRanks, id]);

  const chartConfig = {
    A: {
      label: "Performance",
      color: "hsl(var(--primary))",
    },
  };

  if (loading) {
    return <p className="text-center py-8 text-muted-foreground">Loading...</p>;
  }

  if (!profileData) {
    return <p className="text-center py-8 text-muted-foreground">Player not found</p>;
  }

  const {
    playerName,
    nickname,
    avatarUrl,
    sessions,
    stats,
    radarData,
    consistencyRadarData,
    badges,
    trendData,
    totalBuyInAmount,
    totalNet,
    placementData
  } = profileData;

  return (
    <div className="py-6 space-y-8 pb-20">
      {/* 1. Hero Section */}
      <div className="relative flex flex-col items-center pt-4">
        <Button 
          size="icon" 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="absolute top-0 left-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        
        <div className="relative group">
          <PlayerAvatar
            name={playerName}
            avatarUrl={avatarUrl}
            size={140}
            className="size-[140px] sm:size-[160px] shadow-xl ring-4 ring-background"
          />
        </div>
        
        <div className="mt-4 text-center">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">{playerName}</h1>
          {nickname && (
            <p className="text-lg text-muted-foreground font-medium mt-1 italic">
              "{nickname}"
            </p>
          )}
        </div>
      </div>

      {/* 2. Lifetime Stats Card */}
      <Card className="border-0 shadow-lg overflow-hidden">
        <CardHeader className="bg-muted/30 pb-4">
          <CardTitle className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
            Lifetime Stats
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-muted/50 p-4 rounded-xl border border-border/50">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Win Rate</p>
              <p className="text-2xl font-black">{stats.winRate.toFixed(1)}%</p>
            </div>
            <div className="bg-muted/50 p-4 rounded-xl border border-border/50">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">Sessions</p>
              <p className="text-2xl font-black">{stats.totalSessions}</p>
            </div>
            <div className="bg-success/5 p-4 rounded-xl border border-success/20">
              <div className="flex items-center gap-1.5 mb-1">
                <Trophy className="h-3.5 w-3.5 text-success" />
                <p className="text-xs text-success font-bold uppercase tracking-wider">Best</p>
              </div>
              <p className="text-2xl font-black text-success font-mono">{formatCurrency(stats.maxWin)}</p>
            </div>
            <div className="bg-destructive/5 p-4 rounded-xl border border-destructive/20">
              <div className="flex items-center gap-1.5 mb-1">
                <TrendingDown className="h-3.5 w-3.5 text-destructive" />
                <p className="text-xs text-destructive font-bold uppercase tracking-wider">Worst</p>
              </div>
              <p className="text-2xl font-black text-destructive font-mono">{formatCurrency(stats.maxLoss)}</p>
            </div>
          </div>

          <div className="pt-6 border-t border-dashed border-border/60">
            <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-4">
              Unsettled Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center bg-muted/30 p-3 rounded-lg">
                <span className="text-sm font-medium">Total Entries Cost</span>
                <span className="font-mono font-bold text-lg">{formatCurrency(totalBuyInAmount)}</span>
              </div>
              <div className="flex justify-between items-center bg-muted/30 p-3 rounded-lg">
                <span className="text-sm font-medium">Net Score</span>
                <span
                  className={cn(
                    'font-mono font-bold text-lg',
                    totalNet > 0 ? 'text-success' : totalNet < 0 ? 'text-destructive' : 'text-muted-foreground'
                  )}
                >
                  {totalNet >= 0 ? '+' : ''}{formatCurrency(totalNet)}
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 3. Session History */}
      <div className="space-y-4">
        <div className="flex items-center justify-between px-1">
          <h2 className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
            Session History
          </h2>
          <Badge variant="secondary" className="font-mono">{sessions.length}</Badge>
        </div>
        
        {sessions.length === 0 ? (
          <Card className="border-dashed border-2 bg-transparent">
            <CardContent className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <p className="text-sm font-medium">No sessions recorded yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {sessions.map((s) => (
              <Card
                key={s.sessionId}
                className="group border-0 shadow-sm hover:shadow-md transition-all active:scale-[0.98] cursor-pointer"
                onClick={() => navigate(isPublic ? `/public/${targetUserId}/session/${s.sessionId}` : `/session/${s.sessionId}`)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        s.status === 'unsettled' ? "bg-yellow-400 animate-pulse" : "bg-green-500"
                      )} />
                      <p className="text-sm font-bold">
                        {s.date ? format(new Date(s.date), 'MMM d, yyyy') : 'Unknown date'}
                      </p>
                    </div>
                    <Badge variant={s.status === 'unsettled' ? "outline" : "secondary"} className="text-[10px] uppercase font-bold tracking-tighter">
                      {s.status}
                    </Badge>
                  </div>
                  <div className="flex justify-between items-end">
                    <div className="space-y-0.5">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Entries Cost</p>
                      <p className="text-sm font-semibold">{formatCurrency(s.buyIns * s.buyInUnit)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-0.5">P&L</p>
                      <p className={cn(
                        'font-mono font-black text-base',
                        s.net > 0 ? 'text-success' : s.net < 0 ? 'text-destructive' : 'text-muted-foreground'
                      )}>
                        {s.net >= 0 ? '+' : ''}{formatCurrency(s.net)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* 4. Achievements & Actions Card */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-card to-muted/20">
        <CardHeader>
          <CardTitle className="text-sm font-bold text-muted-foreground uppercase tracking-widest">
            Achievements & Actions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {settings.milestone_badges && (
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex flex-col gap-1">
                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Global Rank</p>
                <Badge variant="outline" className={cn("text-base px-3 py-1 font-black shadow-sm", stats.rankColor)}>
                  {stats.rank}
                </Badge>
              </div>
              
              {stats.streakType !== 'none' && stats.currentStreak >= 3 && (
                <div className="flex flex-col gap-1">
                  <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Current Streak</p>
                  <Badge 
                    variant={stats.streakType === 'win' ? 'default' : 'destructive'} 
                    className="text-base px-3 py-1 gap-1.5 shadow-sm animate-pulse"
                  >
                    {stats.streakType === 'win' ? <Flame className="h-4 w-4 fill-current" /> : <Snowflake className="h-4 w-4" />}
                    <span className="font-black">{stats.currentStreak}</span>
                  </Badge>
                </div>
              )}
            </div>
          )}

          {settings.achievement_badges && badges.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Earned Badges</p>
              <BadgeDisplay badges={badges} />
            </div>
          )}
        </CardContent>
        {!isPublic && (
          <CardFooter className="pt-2">
            <Button
              className="w-full gap-2 font-bold shadow-md hover:shadow-lg transition-all"
              onClick={() => {
                const lines = [
                  `🃏 ${playerName} — SSC Score Stats`,
                  `Sessions: ${stats.totalSessions}`,
                  `Net P&L: ${stats.totalNet >= 0 ? '+' : ''}${stats.totalNet.toFixed(2)}`,
                  `Win Rate: ${Math.round(stats.winRate)}%`,
                  `Best Session: +${stats.maxWin.toFixed(2)}`,
                ];
                window.open(`https://wa.me/?text=${encodeURIComponent(lines.join('\n'))}`, '_blank');
              }}
            >
              <Share2 className="h-4 w-4" /> Share Stats
            </Button>
          </CardFooter>
        )}
      </Card>

      {/* 5. Performance Charts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Performance Radar */}
        <Card className="border-0 shadow-sm overflow-hidden bg-card/50">
          <CardHeader className="p-4 pb-0">
            <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
              Performance Radar
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex justify-center items-center">
            <ChartContainer config={chartConfig} className="h-[250px] w-full">
              <RadarChart data={radarData} margin={{ top: 0, right: 30, left: 30, bottom: 0 }}>
                <ChartTooltip content={<ChartTooltipContent />} />
                <PolarGrid strokeOpacity={0.1} />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fontWeight: 600 }} />
                <Radar
                  name="Player"
                  dataKey="A"
                  stroke="var(--color-A)"
                  fill="var(--color-A)"
                  fillOpacity={0.6}
                />
              </RadarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Consistency Radar */}
        <Card className="border-0 shadow-sm overflow-hidden bg-card/50">
          <CardHeader className="p-4 pb-0">
            <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
              Consistency Radar
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 flex justify-center items-center">
            <ChartContainer
              config={{ A: { label: "Score", color: "hsl(var(--chart-2))" } }}
              className="h-[250px] w-full"
            >
              <RadarChart data={consistencyRadarData} margin={{ top: 0, right: 30, left: 30, bottom: 0 }}>
                <ChartTooltip content={<ChartTooltipContent />} />
                <PolarGrid strokeOpacity={0.1} />
                <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fontWeight: 600 }} />
                <Radar
                  name="Consistency"
                  dataKey="A"
                  stroke="hsl(var(--chart-2))"
                  fill="hsl(var(--chart-2))"
                  fillOpacity={0.5}
                />
              </RadarChart>
            </ChartContainer>
          </CardContent>
        </Card>

        {/* Win/Loss Trend */}
        {settings.profit_loss_trend && (
          <Card className="border-0 shadow-sm overflow-hidden bg-card/50">
            <CardHeader className="p-4 pb-0">
              <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                Win/Loss Trend (last 10)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ChartContainer
                config={{ net: { label: "Cumulative Score", color: "hsl(var(--primary))" } }}
                className="h-[250px] w-full"
              >
                <LineChart data={trendData} margin={{ top: 25, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.1)" />
                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", fontWeight: 500 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", fontWeight: 500 }}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Line
                    type="monotone"
                    dataKey="net"
                    stroke="hsl(var(--primary))"
                    strokeWidth={3}
                    dot={{ r: 4, fill: "hsl(var(--primary))", strokeWidth: 0 }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                </LineChart>
              </ChartContainer>
            </CardContent>
          </Card>
        )}

        {/* Placement Distribution */}
        {placementData.some(d => d.count > 0) && (
          <Card className="border-0 shadow-sm overflow-hidden bg-card/50">
            <CardHeader className="p-4 pb-0">
              <CardTitle className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                Placement Distribution
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ChartContainer
                config={{ count: { label: "Sessions", color: "hsl(var(--primary))" } }}
                className="h-[250px] w-full"
              >
                <BarChart data={placementData} margin={{ top: 25, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--muted-foreground)/0.1)" />
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))", fontWeight: 500 }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                    tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))", fontWeight: 500 }}
                  />
                  <Tooltip
                    cursor={{ fill: 'hsl(var(--muted)/0.3)' }}
                    content={({ active, payload }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-popover border border-border p-2 rounded-lg shadow-md text-xs font-bold">
                            {payload[0].value} sessions
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={40}>
                    <Cell fill="hsl(var(--chart-1))" />
                    <Cell fill="hsl(var(--muted-foreground))" />
                    <Cell fill="hsl(var(--destructive))" />
                  </Bar>
                </BarChart>
              </ChartContainer>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
